import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select-modern';

import type { IPredicate, TComparison } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { UniversalSelect, zIndex } from '@feathr/components';
import { cssVar } from '@feathr/hooks';

import type { IAttrOption, IComparisonChange, IComparisonOption, TOptionsGroup } from '../utils';
import { getComparisonOptions, getDateComparisonOptionsMap, isExistenceComparison } from '../utils';

import * as styles from './ComparisonSelect.css';

/*
 * Date-triggered sends compare a date attribute relative to now, so their date
 * select offers only the relative-date and today options (see
 * getDateComparisonOptionsMap for the ids).
 */
const DATE_TRIGGER_OPTION_IDS = ['_date_r', 'today'];

interface IProps {
  disabled?: boolean;
  /*
   * Emits the selected comparison, plus a value only when the option itself
   * encodes one ("today" = 0, relative date default = 1). All other value
   * derivation (date initial values, existence clearing, list coercion) is
   * owned by deriveComparisonChange in the owner.
   */
  handleChangeComparison: (change: IComparisonChange) => void;
  optionsGroup?: TOptionsGroup;
  predicate: IPredicate;
  rule?: IAttrOption;
}

export function ComparisonOption(props: any): JSX.Element {
  const { name } = props.data;
  const { Option } = components;
  return (
    <Option {...props}>
      <span className={styles.comparison}>{name}</span>
    </Option>
  );
}

function ComparisonSelect({
  disabled,
  handleChangeComparison,
  optionsGroup,
  predicate,
  rule,
}: Readonly<IProps>): JSX.Element | null {
  const { t } = useTranslation();

  const isDateTrigger = optionsGroup === 'emailCampaignDateTrigger';
  const options = useMemo(() => (rule ? getComparisonOptions(rule, t) : []), [rule, t]);
  const dateComparisonOptionsMap = useMemo(() => getDateComparisonOptionsMap(t), [t]);

  // We don't want to show comparison if a rule hasn't been selected.
  if (!rule) {
    return null;
  }

  const { type } = rule;

  /*
   * Date rules split the comparison across a second select; its options and
   * current selection are derived — not stored — from the predicate's comparison
   * and value, and only for date rules (other types never read them). Selecting
   * an option emits the comparison/value changes through handleChangeComparison,
   * and the fresh predicate coming back from the owner drives what it displays.
   */
  let dateComparisonOptions: ISelectOption[] = [];
  let dateTimeSelectOption: ISelectOption | undefined;
  if (type === 'date') {
    dateComparisonOptions = Object.values(dateComparisonOptionsMap).filter(
      ({ id }) => !isDateTrigger || DATE_TRIGGER_OPTION_IDS.includes(id),
    );
    if (predicate.comparison?.endsWith('_date_r')) {
      dateTimeSelectOption =
        predicate.value === 0
          ? dateComparisonOptionsMap.today
          : dateComparisonOptionsMap.relativeDate;
    } else if (predicate.comparison?.endsWith('_date')) {
      dateTimeSelectOption = dateComparisonOptionsMap.exactDate;
    } else {
      dateTimeSelectOption = dateComparisonOptionsMap.exactDateAndTime;
    }
  }

  function handleSelectComparison({ id }: IComparisonOption): void {
    handleChangeComparison({ comparison: id });
  }

  function handleTimeSelectComparison({ id }: ISelectOption): void {
    /*
     * Prevent excessive concatenation of comparison when clicking the same option.
     * Grab the first substring before the underscore.
     * ie: `gt_date_r` could turn into `gt_date_r_date` without this check.
     */
    let newComparison = predicate.comparison?.split('_').shift();
    let value: IPredicate['value'];

    if (id === 'date_time') {
      // Do nothing and use newComparison as is
    } else if (id === 'today') {
      newComparison = `${newComparison}_date_r`;
      value = 0;
    } else {
      // Relative date `_date_r` and exact date `_date`
      newComparison = `${newComparison}${id}`;

      // Default to one day selected for relative date to play nicely with today
      if (id === '_date_r') {
        value = 1;
      }
    }

    handleChangeComparison({ comparison: newComparison as TComparison, value });
  }

  const comparison = options.find(({ id }) => id === predicate.comparison);
  /*
   * If we're using a date comparison, we need to split the comparison between the
   * two dropdowns to determine what kind of date it is: relative, exact, time etc.
   */
  const dateComparison = options.find(({ id }) => id === predicate.comparison?.split('_').shift());

  const selectStyles = {
    /*
     * Margin-right here spaces the attr_against container from
     * the comparison operator container
     */
    container: (provided) => ({ ...provided, marginRight: cssVar('--spacing-1') }),
    menu: (provided) => ({ ...provided, width: 'auto' }),
    singleValue: (provided) => ({
      ...provided,
      transform: 'none',
      position: 'relative',
      top: 0,
      maxWidth: '100%',
      cursor: 'pointer',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      transform: 'none',
      position: 'relative',
      top: 0,
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: zIndex.popover }),
  };

  return (
    <>
      <UniversalSelect<IComparisonOption>
        className={classNames(styles.select, styles.comparisonSelect)}
        components={{ Option: ComparisonOption }}
        // "is" is the only comparison for date-triggered sends.
        disabled={disabled || isDateTrigger}
        menuPortalTarget={process.env.NODE_ENV === 'test' ? undefined : document.body}
        menuPosition={'fixed'}
        name={'predicate_comparison'}
        onSelectSingle={handleSelectComparison}
        options={options}
        styles={selectStyles}
        value={type === 'date' ? dateComparison : comparison}
      />
      {/* Don't show date options for exists and nexists */}
      {type === 'date' && !isExistenceComparison(predicate.comparison) && (
        <UniversalSelect<ISelectOption>
          className={classNames(styles.select, styles.dateTimeTypeSelect)}
          components={{ Option: ComparisonOption }}
          disabled={disabled}
          menuPortalTarget={process.env.NODE_ENV === 'test' ? undefined : document.body}
          menuPosition={'fixed'}
          name={'predicate_comparison_date_time_type'}
          onSelectSingle={handleTimeSelectComparison}
          options={dateComparisonOptions}
          styles={selectStyles}
          value={dateTimeSelectOption}
        />
      )}
    </>
  );
}

export default observer(ComparisonSelect);
